import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Headphones } from 'lucide-react';
import type { PodcastEpisodeView } from '@almanac/contracts';
import { useApi } from '../api/useApi';
import type { DigestSummary } from '../api/client';
import { useAlmanacSearch } from '../components/AppShell';
import { filterDigestSummaries } from '../lib/search';
import { formatMonthDay, formatOrdinalDate, formatWeekdayShort, toLocalYmd } from '../lib/format';
import { ALMANAC_DATA_CHANGED } from '../lib/data-events';
import { episodeDay } from '../lib/podcast';
import { LatestEpisodes } from '../components/podcast/EpisodeSurfaces';
import { usePodcastLibrary } from '../components/podcast/hooks';

const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

function shiftMonth(monthKey: string, offset: number): string {
  const [year, month] = monthKey.split('-').map(Number) as [number, number];
  const date = new Date(year, month - 1 + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The Almanac archive homepage: month calendars marking digest days (and days
 * with unaddressed urgent items) plus an "All digests" list. The digest list is
 * newest-first from the API; all date math parses `YYYY-MM-DD` parts locally to
 * avoid UTC day-shift.
 */
export function DigestListPage() {
  const api = useApi();
  const { query, filters, setLocalEntries, setSectionOptions, openSearch } = useAlmanacSearch();
  const [digests, setDigests] = useState<DigestSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadDigests = useCallback(() => {
    return api
      .listDigests()
      .then((r) => setDigests(r.digests))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [api]);

  useEffect(() => {
    void loadDigests();
    const refresh = () => void loadDigests();
    window.addEventListener(ALMANAC_DATA_CHANGED, refresh);
    return () => window.removeEventListener(ALMANAC_DATA_CHANGED, refresh);
  }, [loadDigests]);

  const visibleDigests = digests ? filterDigestSummaries(digests, query, filters) : null;

  useEffect(() => {
    setSectionOptions([]);
    setLocalEntries(
      (digests ?? []).map((digest) => ({
        id: `${digest.date}:digest`,
        label: digest.title ?? formatMonthDay(digest.date),
        description: formatMonthDay(digest.date),
        keywords: `${digest.date} ${digest.sources.join(' ')}`,
        href: `/digests/${digest.date}`,
      })),
    );
    return () => setLocalEntries([]);
  }, [digests, setLocalEntries, setSectionOptions]);

  const today = useMemo(() => toLocalYmd(new Date()), []);
  const currentMonth = today.slice(0, 7);
  const [visibleMonth, setVisibleMonth] = useState(currentMonth);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toLocalYmd(d);
  }, []);

  // Digest lookup by date + month grouping. Digests arrive newest-first, so
  // the first/last entries bound the range.
  const byDate = useMemo(() => {
    const map = new Map<string, DigestSummary>();
    for (const d of visibleDigests ?? []) map.set(d.date, d);
    return map;
  }, [visibleDigests]);

  const visibleMonthDigestCount = useMemo(
    () => (visibleDigests ?? []).filter((digest) => digest.date.startsWith(visibleMonth)).length,
    [visibleDigests, visibleMonth],
  );

  const unaddressedDays = useMemo(
    () => (visibleDigests ?? []).filter((d) => d.urgent_unaddressed > 0).length,
    [visibleDigests],
  );

  // Episodes are an extra on this page: a failed or slow load leaves the
  // calendar as it was.
  const { state: podcast } = usePodcastLibrary();
  const episodes = useMemo(
    () => (podcast.status === 'ready' ? podcast.library.episodes : []),
    [podcast],
  );
  const episodesByDate = useMemo(() => {
    const map = new Map<string, PodcastEpisodeView[]>();
    for (const episode of episodes) {
      const day = episodeDay(episode);
      if (!day) continue;
      map.set(day, [...(map.get(day) ?? []), episode]);
    }
    return map;
  }, [episodes]);

  const latestDate = visibleDigests && visibleDigests.length > 0 ? visibleDigests[0]!.date : null;
  const oldestDate =
    visibleDigests && visibleDigests.length > 0
      ? visibleDigests[visibleDigests.length - 1]!.date
      : null;

  return (
    <div>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {!digests && !error ? (
          <div className="space-y-4" aria-hidden>
            <div className="h-9 w-48 animate-pulse rounded bg-muted" />
            <div className="grid gap-6 md:grid-cols-2">
              <div className="h-64 animate-pulse rounded-xl bg-muted" />
              <div className="h-64 animate-pulse rounded-xl bg-muted" />
            </div>
          </div>
        ) : null}

        {visibleDigests && visibleDigests.length > 0 ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
                  Daily Digests
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {visibleDigests.length} digests
                  {oldestDate && latestDate
                    ? ` · ${formatMonthDay(oldestDate)} – ${formatMonthDay(latestDate)}`
                    : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openSearch}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
                >
                  Search
                </button>
                {latestDate ? (
                  <Link
                    to={`/digests/${latestDate}`}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
                  >
                    Latest digest →
                  </Link>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-primary" />
                Has digest
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-danger" />
                Unaddressed urgent
              </span>
              {episodes.length > 0 ? (
                <span className="flex items-center gap-1.5">
                  <Headphones className="size-3 text-primary" aria-hidden />
                  Podcast episode
                </span>
              ) : null}
            </div>

            {unaddressedDays > 0 ? (
              <div className="mt-4 rounded-md border-l-2 border-danger bg-danger/10 px-4 py-3 text-sm text-foreground">
                <strong className="font-semibold">
                  {unaddressedDays} {unaddressedDays === 1 ? 'day' : 'days'}
                </strong>{' '}
                with unaddressed urgent items — marked below.
              </div>
            ) : null}

            <LatestEpisodes episodes={episodes} className="mt-6" />

            <div className="mt-6">
              <MonthCalendar
                monthKey={visibleMonth}
                digestDays={visibleMonthDigestCount}
                byDate={byDate}
                episodesByDate={episodesByDate}
                today={today}
                isCurrentMonth={visibleMonth === currentMonth}
                onPrevious={() => setVisibleMonth((month) => shiftMonth(month, -1))}
                onToday={() => setVisibleMonth(currentMonth)}
                onNext={() => setVisibleMonth((month) => shiftMonth(month, 1))}
              />
            </div>

            <h2 className="mt-8 font-display text-lg font-semibold text-foreground">All digests</h2>
            <div className="mt-3 divide-y divide-border/60 overflow-hidden rounded-xl border border-border bg-card">
              {visibleDigests.map((d) => (
                <AllDigestRow key={d.date} digest={d} today={today} yesterday={yesterday} />
              ))}
            </div>
          </>
        ) : null}

        {digests?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No digests yet.</p>
        ) : null}
        {digests && digests.length > 0 && visibleDigests?.length === 0 ? (
          <div className="space-y-2 rounded-xl bg-muted/50 p-6 text-center">
            <p className="text-base font-medium text-foreground">No matching digests</p>
            <p className="text-sm text-muted-foreground">
              Change the query or clear filters to restore more days.
            </p>
          </div>
        ) : null}
        {/* With no digests to show (none yet, or none match a search), the
            episodes still belong on the page: search filters digests only. */}
        {visibleDigests && visibleDigests.length === 0 ? (
          <LatestEpisodes episodes={episodes} className="mt-6" />
        ) : null}
      </main>
    </div>
  );
}

/**
 * One traditional month view: Monday-first weekday labels and a stable
 * whole-week grid that includes adjacent-month dates for week-level context.
 */
function MonthCalendar({
  monthKey,
  digestDays,
  byDate,
  episodesByDate,
  today,
  isCurrentMonth,
  onPrevious,
  onToday,
  onNext,
}: {
  monthKey: string;
  digestDays: number;
  byDate: Map<string, DigestSummary>;
  episodesByDate: Map<string, PodcastEpisodeView[]>;
  today: string;
  isCurrentMonth: boolean;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
}) {
  const [year, month] = monthKey.split('-').map(Number) as [number, number];
  const firstDate = new Date(year, month - 1, 1);
  const monthLabel = firstDate.toLocaleDateString(undefined, { month: 'long' });
  const mondayOffset = (firstDate.getDay() + 6) % 7;
  const gridStart = new Date(year, month - 1, 1 - mondayOffset);
  const daysInMonth = new Date(year, month, 0).getDate();
  const cellCount = Math.ceil((mondayOffset + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: cellCount }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
  const digestLabel = `${digestDays} ${digestDays === 1 ? 'digest' : 'digests'}`;

  return (
    <section
      aria-label={`Calendar for ${monthLabel} ${year}`}
      data-calendar-month={monthKey}
      className="rounded-xl border border-border bg-card p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-balance font-display text-lg font-semibold text-foreground">
            {monthLabel} {year}
          </h2>
          <p className="text-base text-muted-foreground tabular-nums sm:text-sm">{digestLabel}</p>
        </div>
        <div className="flex items-center gap-1" aria-label="Calendar navigation">
          <button
            type="button"
            onClick={onPrevious}
            className="rounded-md px-2.5 py-2 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:py-1.5 sm:text-sm"
            aria-label="Previous month"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onToday}
            disabled={isCurrentMonth}
            className="rounded-md px-2.5 py-2 text-base font-medium text-primary hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:text-muted-foreground/50 disabled:hover:bg-transparent sm:py-1.5 sm:text-sm"
            aria-label="Show current month"
          >
            Today
          </button>
          <button
            type="button"
            onClick={onNext}
            className="rounded-md px-2.5 py-2 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:py-1.5 sm:text-sm"
            aria-label="Next month"
          >
            Next
          </button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((weekday) => (
          <div
            key={weekday}
            className="text-center text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {weekday}
          </div>
        ))}
        {cells.map((date) => {
          const dateStr = toLocalYmd(date);
          return (
            <DayCell
              key={dateStr}
              day={date.getDate()}
              dateStr={dateStr}
              digest={byDate.get(dateStr) ?? null}
              hasEpisode={episodesByDate.has(dateStr)}
              isToday={dateStr === today}
              isOutsideMonth={date.getMonth() !== month - 1}
            />
          );
        })}
      </div>
    </section>
  );
}

/**
 * A single calendar day. Plain muted number when there's no digest; an outlined
 * linked cell with a dot when a digest exists; a red-outlined cell with a
 * count badge when the day has unaddressed urgent items. Today gets a ring.
 */
function DayCell({
  day,
  dateStr,
  digest,
  hasEpisode,
  isToday,
  isOutsideMonth,
}: {
  day: number;
  dateStr: string;
  digest: DigestSummary | null;
  hasEpisode: boolean;
  isToday: boolean;
  isOutsideMonth: boolean;
}) {
  const ring = isToday ? ' ring-2 ring-primary' : '';
  const episodeMark = hasEpisode ? (
    <Headphones
      className="absolute top-1 left-1 size-3 text-primary sm:top-1.5 sm:left-1.5"
      aria-label="Podcast episode"
    />
  ) : null;
  const todayLabel = isToday ? (
    <span className="text-[8px] font-semibold tracking-wider text-primary">TODAY</span>
  ) : null;

  if (!digest) {
    return (
      <div
        data-date={dateStr}
        className={`relative flex min-h-12 flex-col items-center justify-center rounded-lg text-sm tabular-nums sm:min-h-20 ${isOutsideMonth ? 'text-muted-foreground/30' : 'text-muted-foreground/60'}${ring}`}
      >
        {episodeMark}
        {todayLabel}
        <span>{day}</span>
      </div>
    );
  }

  const hasUnaddressed = digest.urgent_unaddressed > 0;
  const base = hasUnaddressed
    ? 'relative flex min-h-12 flex-col items-center justify-center rounded-lg border border-danger/50 bg-primary/5 text-foreground hover:border-danger sm:min-h-20'
    : 'relative flex min-h-12 flex-col items-center justify-center rounded-lg border border-primary/40 bg-primary/5 text-foreground hover:border-primary sm:min-h-20';

  return (
    <Link
      to={`/digests/${dateStr}`}
      data-date={dateStr}
      className={`${base}${isOutsideMonth ? ' opacity-50' : ''}${ring}`}
    >
      {episodeMark}
      {todayLabel}
      <span className="text-sm">{day}</span>
      <span className="mt-0.5 size-1 rounded-full bg-primary" />
      {hasUnaddressed ? (
        <span className="absolute right-0 top-0 grid size-4 -translate-y-1/2 translate-x-1/2 place-items-center rounded-full bg-danger text-[10px] font-semibold text-danger-foreground">
          {digest.urgent_unaddressed}
        </span>
      ) : null}
    </Link>
  );
}

/** One row in the "All digests" list: weekday, long date, urgent/relative chips, arrow. */
function AllDigestRow({
  digest,
  today,
  yesterday,
}: {
  digest: DigestSummary;
  today: string;
  yesterday: string;
}) {
  let relativeLabel: string | null = null;
  if (digest.date === today) {
    relativeLabel = 'TODAY';
  } else if (digest.date === yesterday) {
    relativeLabel = 'YESTERDAY';
  }

  return (
    <Link
      to={`/digests/${digest.date}`}
      className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/50"
    >
      <span className="w-10 text-xs font-semibold tracking-wider text-muted-foreground">
        {formatWeekdayShort(digest.date).toUpperCase()}
      </span>
      <span className="text-sm font-medium text-foreground">{formatOrdinalDate(digest.date)}</span>
      <span className="flex-1" />
      {digest.urgent_unaddressed > 0 ? (
        <span className="rounded-full bg-danger/15 px-2 py-0.5 text-[11px] font-semibold text-danger">
          {digest.urgent_unaddressed} URGENT
        </span>
      ) : null}
      {relativeLabel ? (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
          {relativeLabel}
        </span>
      ) : null}
      <span className="text-muted-foreground">→</span>
    </Link>
  );
}
